/* eslint-disable max-nested-callbacks */
import { groupBy, mean, round, sumBy, keys } from 'lodash'
import React, { useMemo } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import { getYear } from 'utils/DateUtil'
import PropTypes from 'prop-types'
import QualityChart from 'components/echart/QualityChart'
import moment from 'moment'
import DtoAnalysisLight from 'quality/dto/analyse/DtoAnalysisLight'
import { AREA } from 'quality/constants/ChartConstant'

const PesticidesChartAnnualEvolution = ({
    analysis = {},
    thresholds = [],
    name = '',
    unit = '',
    startPeriod,
    endPeriod,
}) => {
    const {
        qualitometers,
        cities,
    } = useSelector(store => ({
        qualitometers: store.QualityReducer.qualitometersLight,
        cities: store.CityReducer.cities,
    }), shallowEqual)

    const dataAnalysisFormated = useMemo(() => keys(analysis).map(key => {
        const { qualitometer: qualitometerId } = analysis[key][0]
        const qualitometer = qualitometers.find(q => q.id === qualitometerId)
        const city = qualitometer && cities.find(c => c.code === qualitometer.townCode)
        const yearGrounp = groupBy(analysis[key], a => getYear(a.sampleDate))
        return {
            name: [qualitometer?.code ? `[${qualitometer.code}]` : '', `${qualitometer?.name || ''}`, `${city?.name ? `- ${city.name}` : ''}`].join(' '),
            unit,
            dataList: keys(yearGrounp).map(year => {
                const operationGroup = groupBy(yearGrounp[year], a => a.operation)
                const average = mean(keys(operationGroup).map(operationId => sumBy(operationGroup[operationId], 'result')))
                return { date: moment(year, 'YYYY').startOf('year').valueOf(), value: round(average, 5) }
            }),
        }
    }), [analysis, qualitometers, unit, cities])

    const nameWithUnit = `${name} (${unit})`
    return (
        <QualityChart
            title={i18n.annualPesticidesTrendsCompared}
            exportName={nameWithUnit}
            grids={[{ height: 250 }]}
            xAxis={[{ min: startPeriod, max: endPeriod }]}
            yAxis={[{
                name: nameWithUnit,
                min: 0,
                nameGap: dataAnalysisFormated.every(data => !data.dataList.length) ? 25 : 50,
            }]}
            series={dataAnalysisFormated}
            thresholds={thresholds}

            roundValue={5}

            defaultDisplayLine={false}
            defaultStateThreshold={AREA}

            withToolQuantification
            withToolTypeLine
            withToolTypeBar
            withArea
            withToolThreshold
            withToolFullScreen
            withToolZoom='PESTICIDES_CHART_ANNUAL_EVOLUTION'
        />
    )
}

PesticidesChartAnnualEvolution.propTypes = {
    analysis: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.instanceOf(DtoAnalysisLight))),
    thresholds: PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.number,
        name: PropTypes.string,
        color: PropTypes.string,
    })),
    areaColor: PropTypes.arrayOf(PropTypes.string),
    name: PropTypes.string,
    unit: PropTypes.string,
    startPeriod: PropTypes.number,
    endPeriod: PropTypes.number,
}

const NitratesChartAnnualEvolution = ({
    analysis = {},
    thresholds = [],
    name = '',
    unit = '',
    startPeriod,
    endPeriod,
}) => {
    const {
        qualitometers,
        cities,
    } = useSelector(store => ({
        qualitometers: store.QualityReducer.qualitometersLight,
        cities: store.CityReducer.cities,
    }), shallowEqual)

    const analysisFormated = useMemo(() => keys(analysis).map(key => {
        const { qualitometer: qualitometerId } = analysis[key][0]
        const qualitometer = qualitometers.find(q => q.id === qualitometerId)
        const city = qualitometer && cities.find(c => c.code === qualitometer.townCode)
        const yearGrounp = groupBy(analysis[key], a => getYear(a.sampleDate))
        return {
            name: [qualitometer?.code ? `[${qualitometer.code}]` : '', `${qualitometer?.name || ''}`, `${city?.name ? `- ${city.name}` : ''}`].join(' '),
            unit,
            dataList: keys(yearGrounp).map(year => {
                const total = sumBy(yearGrounp[year], 'result')
                const averageTotal = total / yearGrounp[year].length
                return { date: moment(year, 'YYYY').startOf('year').valueOf(), value: round(averageTotal, 5) }
            }),
        }
    }), [analysis, qualitometers, unit, cities])

    const nameWithUnit = `${name} (${unit})`
    return (
        <QualityChart
            title={i18n.annualNitratesTrendsCompared}
            exportName={nameWithUnit}
            grids={[{ height: 250 }]}
            xAxis={[{ min: startPeriod, max: endPeriod }]}
            yAxis={[{ name: nameWithUnit, min: 0 }]}
            series={analysisFormated}
            thresholds={thresholds}

            roundValue={5}

            defaultDisplayLine={false}
            defaultStateThreshold={AREA}

            withToolQuantification
            withToolTypeLine
            withToolTypeBar
            withArea
            withToolThreshold
            withToolFullScreen
            withToolZoom='NITRATES_CHART_ANNUAL_EVOLUTION'
        />
    )
}

NitratesChartAnnualEvolution.propTypes = {
    analysis: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.instanceOf(DtoAnalysisLight))),
    thresholds: PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.number,
        name: PropTypes.string,
        color: PropTypes.string,
    })),
    areaColor: PropTypes.arrayOf(PropTypes.string),
    name: PropTypes.string,
    unit: PropTypes.string,
    startPeriod: PropTypes.number,
    endPeriod: PropTypes.number,
}

export { NitratesChartAnnualEvolution, PesticidesChartAnnualEvolution }